<template>

  <div v-if="toolboxGroup">
    <IconInternalData size="24" style="padding:10px;"/>
    <p>Model Text</p>
  </div>
  <div v-else class="text-set">
    <div class="subset" v-if="showLabel && !labelAfter">
      <ProsemirrorEditor
          class="text"
          defaultColor="#333"
          :defaultFontName="theme.fontName"
          :editable="true"
          :viewer="viewer"
          :elementId="elementData.id"
          :value="elementText"
          :ignoreEvents="ignoreProseMirror"
          @update="value => elementData.text = value"
          @focus="proseFocus()"
          :style="getSecondaryStyle()"
      />
      <Idot :element-data="elementData"/>
    </div>



    <div v-if="isEditableMeta" :data-element-id="elementId" class="mode-text-parent"
         @mouseover="isHovering = true"
         @mouseleave="isHovering = false">

      <InputText
          v-model="metaValue"
          :placeholder="metaPlaceholder"
          class="model-text-input"
          @keydown="$event.stopPropagation();"
          @focus="focus()"
          @blur="unfocus()"
          :style="getStyle()"
      />
      <span style="width: 0px; position: relative">
          <span v-if="isHovering || isFocused" style="position: absolute; z-index:9999; left: -2px;"
                class="model-text-pencil"><IconWrite
              style="position: relative; top: -5px; padding: 2px; background-color: white;"
              size="20"></IconWrite></span>
        </span>

    </div>

    <div v-else-if="elementFieldType === 'notes'" :data-element-id="elementId" :style="elementStyle"
         class="mode-text-parent"
         @mouseover="isHovering = true"
         @mouseleave="isHovering = false">
      <div v-if="elementEditorType === 'date'">
        <Calendar class="model-text-input" v-model="notes" dateFormat="mm/dd/yy" :id="elementId + '-calendar'" :placeholder="placeholder" :style="getStyle()"  />
      </div>
      <div v-else>
        <Textarea
            v-model="notes"
            :placeholder="placeholder"
            @keydown="$event.stopPropagation();"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :style="getStyle()"
            :rows="textAreaRows"
            class="model-text-input"
        ></Textarea>
      </div>

    </div>
    <div v-else-if="elementFieldType === 'source'" :data-element-id="elementId" :style="elementStyle"
         class="mode-text-parent"
         @mouseover="isHovering = true"
         @mouseleave="isHovering = false">
        <Textarea
            v-model="getComputedSource"
            @keydown="$event.stopPropagation();"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :style="getStyle()"
            :rows="textAreaRows"
            class="model-text-input"
        ></Textarea>
    </div>
    <div v-else-if="elementFieldType === 'message'" :data-element-id="elementId" :style="elementStyle"
         class="mode-text-parent"
         @mouseover="isHovering = true"
         @mouseleave="isHovering = false">
        <Textarea
            v-model="getComputedMessage"
            @keydown="$event.stopPropagation();"
            @focus="isFocused = true"
            @blur="isFocused = false"
            :style="getStyle()"
            :rows="textAreaRows"
            class="model-text-input"
        ></Textarea>
    </div>
    <div v-else-if="elementFieldType === 'none'" :data-element-id="elementId">

    </div>
    <div v-else-if="!isEditableMeta" :data-element-id="elementId" :style="elementStyle"
         class="mode-text-parent" @mouseover="isHovering = true" @mouseleave="isHovering = false">
      {{ getComputedText }}
    </div>


    <div class="subset" v-if="showLabel && labelAfter">
      <ProsemirrorEditor
          class="text"
          defaultColor="#333"
          :defaultFontName="theme.fontName"
          :editable="true"
          :viewer="viewer"
          :elementId="elementData.id"
          :value="elementText"
          :ignoreEvents="ignoreProseMirror"
          @update="value => elementData.text = value"
          @focus="proseFocus()"
          :style="getSecondaryStyle()"
      />
      <Idot :element-data="elementData"/>
    </div>

  </div>
  <Idot v-if="!showLabel" :element-data="elementData"/>
</template>

<script setup lang="ts">
import {computed, getCurrentInstance, onMounted, ref, toRefs, watch} from 'vue'
import {useGivingTreeStore} from '@/store/givingTree'
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue";
import InputText from "primevue/inputtext";
import {storeToRefs} from "pinia";
import {useSlidesStore, useWidgetStore} from "@/store";
import ProsemirrorEditor from "@/views/components/element/ProsemirrorEditor.vue";
import Idot from "@/components/Idot.vue"

const { theme } = storeToRefs(useSlidesStore())
const givingTreeStore = useGivingTreeStore()




const isHovering = ref(false)
const isFocused = ref(false)
const isEditableMeta = ref(false)
const {inputSelected} = storeToRefs(useWidgetStore())

const props = defineProps({
  elementData: {
    type: Object
  },
  class: String,
  viewer: {
    type: Boolean,
    default: false
  },
  style: Object
})

const focus = () => {
  isFocused.value = true
  inputSelected.value = true
}

const unfocus = () => {
  isFocused.value = false
  setTimeout(() => {
    if (!document.activeElement?.classList.contains('preserve-select-input')) {
      inputSelected.value = false
    }
  }, 100)
}




const {elementData} = toRefs(props)

const elementId = computed(() => elementData.value?.id ?? null)
const elementHookId = computed(() => elementData.value?.hookId ?? '')

const elementStyle = computed(() => {
  const style = elementData.value?.style ?? {};
  const {filter, ...withoutFilter} = style;
  return withoutFilter;
});
const elementText = computed(() => elementData.value?.text ?? '')
const elementFieldType = computed(() => elementData.value?.fieldType ?? '')
const elementEditorType = computed(() => elementData.value?.editorType ?? '')

const modelLabelName = ref('')

const justifyContent = computed(() => elementData.value?.style.justifyContent ?? '')


const textAreaRows = computed(() => elementData.value?.textAreaRowHeight ?? 1)

const labelMargins = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? '0 10px' : '10px 0')
const positionFlex = computed(() => elementData.value?.labelPosition === 'left' || elementData.value?.labelPosition === 'right' ? 'row' : 'column')
const showLabel = computed(() => elementData.value?.labelPosition !== 'none')
const labelAfter = computed(() => elementData.value?.labelPosition === 'bottom' || elementData.value?.labelPosition === 'right')
const elementColor = computed(() => elementStyle.value?.color ?? '')
const placeholderColor = computed(() => elementStyle.value?.placeholderColor ?? '')
const fontSize = computed(() => elementStyle.value?.fontSize ?? '')
const fontWeight = computed(() => elementStyle.value?.fontWeight ?? '')
const fontStyle = computed(() => elementStyle.value?.fontStyle ?? '')
const textAlign = computed(() => elementData.value?.style.justifyContent !== '' ? 'center' : elementData.value?.style.align)
const textDecoration = computed(() => elementStyle.value?.textDecoration ?? '')
const tileAlign = computed(() => elementData.value?.style.align ?? '')
const inputAlign = computed(() => elementData.value?.style.inputAlign ?? '')

const fontName = computed(() => `"${theme.value.fontName}"` ?? '')

const ignoreProseMirror = ref(false)
const getSecondaryStyle = () => {
  return {
    minWidth: '30px',
    maxWidth: '100%',
    width: px(elementData.value.style?.secondaryWidth, ''),
    height: px(elementData.value.style?.secondaryHeight, ''),
  }
}
const proseFocus = () => {
  ignoreProseMirror.value = false
}

const metaValue = computed({
  get() {
    return elementData.value.placeholder ? getOverrideText.value : getComputedText.value
  },
  set(val) {
    getComputedText.value = val
  }
})

const metaPlaceholder = computed(() => {
  if (elementData.value.placeholder) {
    return getComputedText.value
  }
})

const labelName = computed(() => modelLabelName.value !== '' ? modelLabelName.value : elementText.value)

const placeholder = computed(() => {
  if (elementData.value.placeholder) {
    return getComputedNotes.value
  }
})

const notes = computed({
  get() {
    return elementData.value.placeholder ? getOverrideNotes.value : getComputedNotes.value
  },
  set(val) {
    if (val instanceof Date) {
      getComputedNotes.value = (val.getMonth() + 1).toString().padStart(2, '0') + '/' +
          val.getDate().toString().padStart(2, '0') + '/' +
          val.getFullYear()
    } else {
      getComputedNotes.value = val
    }
  }
})


const toolboxGroup = computed(() => {
  const parentClassList = getCurrentInstance().parent.attrs.class;
  return parentClassList === "dragArea toolbox-group"
});

onMounted(() => {
  if (elementHookId.value) {
    updateMapping(elementHookId.value)
  }

})

const {
  getActiveHookId,
  getComputedText,
  getOverrideText,
  getComputedSource,
  getComputedNotes,
  getOverrideNotes,
  getComputedMessage,
} = useComputedTreeValue()

watch(elementHookId, (newVal) => {
  if (newVal) {
    updateMapping(newVal)
  }
}, {deep: true})

const updateMapping = (hookId) => {
  getActiveHookId(hookId)
  const widget = givingTreeStore.getWidgetValue(hookId)

  if (widget) {
    // todo if this is a variable, then have a mapping for source / notes
    isEditableMeta.value = widget.type === 'meta' && !widget.readOnly

  }

}


const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  } else {
    return defaultValue
  }
}

const getStyle = () => {
  return {
    fontFamily: theme.value.fontName ?? '',
    maxWidth: '100%',
    width: px(elementData.value.style?.width, ''),
    height: px(elementData.value.style?.height, ''),
    fontWeight: elementData.value.style?.fontWeight,
    fontStyle: elementData.value.style?.fontStyle,
    textDecoration: elementData.value.style?.textDecoration,
  }
}

</script>
<style lang="scss" scoped>
.model-text-pencil {
  margin-left: -35px;
  color: #333;
  padding: 8px;
  position: relative;
  height: 100%;
}

.model-text-input {
  background-color: transparent;
  font-size: inherit;
  text-align: inherit;
  color: inherit;
  width: 100%;
  border: 1px solid transparent;
  padding: 5px;
  outline: none;
  transition: border 0.1s ease-in-out;
  position: relative;
  font-family: v-bind(fontName);
}

.model-text-input:hover {
  border: 1px solid #DDD;
}

.model-text-input:focus {
  border: 1px solid #CCC;
}

:deep(p) {
  margin: v-bind(labelMargins);
}

.subset {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.text-set {
  display: flex;
  flex-direction: v-bind(positionFlex);
  align-items: v-bind(tileAlign);
  justify-content: v-bind(justifyContent);
}

:deep(.p-inputtext) {
  background-color: transparent;
  color: v-bind(elementColor);
  font-size: v-bind(fontSize);
  font-weight: v-bind(fontWeight);
  font-style: v-bind(fontStyle);
  text-align: v-bind(inputAlign);
  text-decoration: v-bind(textDecoration);
}

:deep(.model-text-input::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 1;
}

:deep(.model-text-input:hover::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 0.8;
}

:deep(.model-text-input:focus::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 0.5;
}

:deep(.p-calendar input::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 1;
}

:deep(.p-calendar input:hover::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 0.8;
}

:deep(.p-calendar input:focus::placeholder) {
  color: v-bind(placeholderColor);
  opacity: 0.5;
}
</style>
